export interface RoadworkItem {
  id?: string | null;
  reference?: string | null;
  description?: string | null;
  location?: string | null;
  full_address?: string | null;
  promoter_name?: string | null;
  status?: string | null;
  traffic_impact?: string | null;
  works_type?: string | null;
  planned_start?: string | null;
  planned_end?: string | null;
  latest_start?: string | null;
  latest_end?: string | null;
  duration?: number | string | null;
  url?: string | null;
  tags?: string[];
  latitude?: number | null;
  longitude?: number | null;
  easting?: string | number | null;
  northing?: string | number | null;
  is_new?: boolean | null;
  first_seen_at?: string | null;
}

export interface RoadworksStatusResponse {
  ok: boolean;
  works_count?: number;
  active_travel_count?: number;
  council_promoter_count?: number;
  unknown_field_rate?: number;
  meta_summary?: Record<string, unknown>;
  meta_input?: Record<string, unknown>;
  quality_report?: Record<string, unknown> | null;
  last_run?: Record<string, unknown> | null;
  freshness?: Record<string, unknown> | null;
  continuity?: Record<string, unknown> | null;
}

type FetchLike = typeof fetch;

type RequestOptions = {
  signal?: AbortSignal;
  retries?: number;
  timeoutMs?: number;
  fetchImpl?: FetchLike;
};

export type FetchRoadworksWorksOptions = RequestOptions & {
  limit?: number;
  activeTravelOnly?: boolean;
};

export type FetchRoadworksStatusOptions = RequestOptions;

type ErrorWithStatus = Error & { status?: number };

const HAS_EXPLICIT_API_BASE = Boolean(
  process.env.NEXT_PUBLIC_ROADWORKS_API_BASE ||
  process.env.NEXT_PUBLIC_API_BASE_URL
);

const DEFAULT_API_BASE =
  process.env.NEXT_PUBLIC_ROADWORKS_API_BASE ||
  process.env.NEXT_PUBLIC_API_BASE_URL ||
  'https://api.city-scope.co.uk';
const DEFAULT_TIMEOUT_MS = 15000;
const DEFAULT_RETRIES = 2;
export const DEFAULT_ROADWORKS_WORKS_LIMIT = 5000;

function normalizeApiBase(rawValue: string): string {
  let value = String(rawValue || '').trim();
  if (!value) {
    return 'https://api.city-scope.co.uk';
  }

  if (value.startsWith('/')) {
    return value.replace(/\/+$/, '');
  }

  value = value.replace(/\/+$/, '');
  const lower = value.toLowerCase();

  if (lower.endsWith('/api/v1/accounts')) {
    return value.slice(0, -'/api/v1/accounts'.length);
  }

  if (lower.endsWith('/api/v1')) {
    return value.slice(0, -'/api/v1'.length);
  }

  return value;
}

function resolveApiBase(): string {
  if (
    typeof window !== 'undefined' &&
    !HAS_EXPLICIT_API_BASE &&
    ['localhost', '127.0.0.1'].includes(window.location.hostname)
  ) {
    return '/dev-api';
  }

  return DEFAULT_API_BASE;
}

function buildRoadworksUrl(path: string): string {
  const base = normalizeApiBase(resolveApiBase());
  if (base.startsWith('/')) {
    return `${base}${path}`;
  }
  return `${base}${path}`;
}

function delay(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

function shouldRetryStatus(status: number): boolean {
  return status === 429 || status >= 500;
}

function mergeSignals(signalA?: AbortSignal, signalB?: AbortSignal): AbortSignal | undefined {
  if (!signalA && !signalB) return undefined;
  if (!signalA) return signalB;
  if (!signalB) return signalA;

  const controller = new AbortController();
  const onAbort = () => controller.abort();
  signalA.addEventListener('abort', onAbort, { once: true });
  signalB.addEventListener('abort', onAbort, { once: true });
  return controller.signal;
}

async function fetchJson<T>(url: string, options: RequestOptions = {}): Promise<T> {
  const {
    signal,
    retries = DEFAULT_RETRIES,
    timeoutMs = DEFAULT_TIMEOUT_MS,
    fetchImpl,
  } = options;
  const effectiveFetch = fetchImpl || fetch;
  let lastError: Error | null = null;

  for (let attempt = 0; attempt <= retries; attempt += 1) {
    const timeoutController = new AbortController();
    const timeoutId = setTimeout(() => timeoutController.abort(), timeoutMs);
    const requestSignal = mergeSignals(signal, timeoutController.signal);

    try {
      const response = await effectiveFetch(url, {
        headers: { Accept: 'application/json' },
        signal: requestSignal,
      });

      if (!response.ok) {
        const httpError: ErrorWithStatus = new Error(`Roadworks API request failed (${response.status})`);
        httpError.status = response.status;
        throw httpError;
      }

      try {
        return (await response.json()) as T;
      } catch {
        throw new Error('Roadworks API returned invalid JSON');
      }
    } catch (error) {
      const isAbortedByCaller = Boolean(signal?.aborted);
      if (isAbortedByCaller) {
        throw new Error('Roadworks request aborted');
      }

      const err = error instanceof Error ? error : new Error('Roadworks request failed');
      const status = (err as ErrorWithStatus).status;
      const isTimeout = timeoutController.signal.aborted;
      const isNetwork = err instanceof TypeError;
      const canRetry =
        attempt < retries &&
        (isTimeout || isNetwork || (typeof status === 'number' && shouldRetryStatus(status)));

      if (!canRetry) {
        throw err;
      }

      lastError = err;
      const backoffMs = Math.min(1000 * Math.pow(2, attempt), 4000);
      await delay(backoffMs);
    } finally {
      clearTimeout(timeoutId);
    }
  }

  throw lastError || new Error('Roadworks request failed');
}

export async function fetchRoadworksWorks(limitOrOptions: number | FetchRoadworksWorksOptions = DEFAULT_ROADWORKS_WORKS_LIMIT): Promise<RoadworkItem[]> {
  const options: FetchRoadworksWorksOptions =
    typeof limitOrOptions === 'number' ? { limit: limitOrOptions } : limitOrOptions;
  const limit = options.limit ?? DEFAULT_ROADWORKS_WORKS_LIMIT;
  const query = new URLSearchParams({
    limit: String(limit),
    sort: 'latest_start_desc',
  });
  if (options.activeTravelOnly) {
    query.set('active_travel_only', 'true');
  }
  const url = buildRoadworksUrl(`/api/v1/roadworks/works?${query.toString()}`);
  const payload = await fetchJson<unknown>(url, options);

  if (Array.isArray(payload)) {
    return payload as RoadworkItem[];
  }

  if (payload && typeof payload === 'object') {
    const items = (payload as { items?: unknown }).items;
    if (Array.isArray(items)) {
      return items as RoadworkItem[];
    }
  }

  throw new Error('Roadworks API returned an unexpected payload shape');
}

export async function fetchRoadworksStatus(options: FetchRoadworksStatusOptions = {}): Promise<RoadworksStatusResponse | null> {
  const url = buildRoadworksUrl('/api/v1/roadworks/status');

  try {
    return await fetchJson<RoadworksStatusResponse>(url, options);
  } catch {
    return null;
  }
}

export function getRoadworksApiBase(): string {
  return normalizeApiBase(resolveApiBase());
}
